import { NO_FILL } from '~/client/src/shared/components/MapMarkups/MarkupDrawController'
import { DEFAULT_FONT_SIZE } from '~/client/src/shared/components/MapMarkups/MarkupTextStore'
import type {
  ArrowheadType,
  FillStyle,
  LineType,
  ShapeKind,
  StrokeStyle,
} from '~/client/src/shared/components/MapMarkups/markupTools'

import Colors from '~/client/src/shared/theme.module.scss'

export interface IPoint {
  x: number
  y: number
}

export interface IRect {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

export interface ISize {
  width: number
  height: number
}

export type PathKind = 'shape' | 'arrow' | 'freehand' | 'highlighter'

export interface IPathMarkup {
  id: string
  kind: PathKind
  shapeKind?: ShapeKind
  lineType?: LineType
  points: IPoint[]
  color: string
  strokeWidth: number
  strokeStyle: StrokeStyle
  fillColor: string
  fillStyle: FillStyle
  arrowheadStart: ArrowheadType
  arrowheadEnd: ArrowheadType
  borderOverridden: boolean
}

export interface ITextStyle {
  color: string
  fillColor: string
  fontSize: number
  bold: boolean
  italic: boolean
  underline: boolean
}

export interface ITextMarkup extends ITextStyle {
  id: string
  kind: 'text'
  at: IPoint
  text: string
  boxWidth: number | null
}

export type PhotoMarkup = IPathMarkup | ITextMarkup

export const MAX_DIMENSION_PX = 4096
export const JPEG_QUALITY = 0.9

const MARKUP_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp']

export const TEXT_FONT_FAMILY = 'Inter, sans-serif'
export const TEXT_LINE_HEIGHT = 1.3
export const TEXT_PADDING = 4
export const TEXT_MIN_WIDTH = 8
export const TEXT_MIN_BOX_WIDTH = 40

export const PHOTO_MARKUP_COLOR = Colors.mapRed

export const DEFAULT_TEXT_STYLE: ITextStyle = {
  color: PHOTO_MARKUP_COLOR,
  fillColor: NO_FILL,
  fontSize: DEFAULT_FONT_SIZE,
  bold: true,
  italic: false,
  underline: false,
}

export function canMarkUpImage(fileName: string): boolean {
  const extension = fileName?.split('.').pop()?.toLowerCase()
  return MARKUP_IMAGE_EXTENSIONS.includes(extension)
}

export function isTextMarkup(markup: PhotoMarkup): markup is ITextMarkup {
  return markup.kind === 'text'
}

export function isFilled(markup: PhotoMarkup): boolean {
  return markup.kind === 'shape' && markup.fillColor !== NO_FILL
}

export function isVertexEdited(markup: PhotoMarkup): boolean {
  return markup.kind === 'arrow'
}

export function isClosedPath(markup: IPathMarkup): boolean {
  return markup.kind === 'shape'
}

export function hasContent(markup: PhotoMarkup): boolean {
  return !isTextMarkup(markup) || !!markup.text.trim()
}

export function defaultArrowheads(lineType: LineType): {
  start: ArrowheadType
  end: ArrowheadType
} {
  return lineType === 'arrow'
    ? { start: 'none', end: 'arrow' }
    : { start: 'none', end: 'none' }
}
